/**
 * EL CLIENTE DEL API — UNA sola pieza.
 *
 * Este archivo existe para que no vuelva a haber `fetch` sueltos repartidos por
 * las pantallas. Ese reparto es la deuda que estamos pagando ahora mismo con
 * Firestore: `getDocs(collection(db,'products'))` está escrito a mano en cada
 * página, cada copia decidió su propio `limit` y su propio filtrado, y por eso
 * el Marketplace filtra 20 artículos en el navegador mientras el dueño cree que
 * filtra el catálogo. Si el API se llama desde diez sitios, dentro de seis meses
 * habrá diez formas distintas de mandar el token, de contar el tiempo y de
 * tratar un 500.
 *
 * Aquí se decide una vez: la dirección, el token, el plazo y qué es un error.
 *
 * ─── LA AUTENTICACIÓN NO SE REHACE ──────────────────────────────────────────
 *
 * Se manda el MISMO `idToken` de Firebase que la app ya tiene, porque el API lo
 * valida con `verifyIdToken()` (api/src/server.ts). No hay cookie, no hay sesión
 * propia, no hay segundo sistema de identidad. Firebase Auth sigue siendo la
 * única autoridad sobre quién es quién.
 *
 * ─── FALLA RUIDOSO, NO VACÍO ────────────────────────────────────────────────
 *
 * Todo camino de error LANZA. Ninguno devuelve `[]`. Una lista vacía es una
 * afirmación sobre el catálogo ("no hay productos") y este módulo no está en
 * condiciones de hacerla cuando lo que pasó fue que no se pudo preguntar. La
 * pantalla es quien decide qué enseñar; ver `LoadFailed.js`.
 */
import { auth } from '../firebase';

/** Plazo por defecto. Un catálogo que tarda más de esto ya falló para el usuario. */
export const API_TIMEOUT_MS = 12000;

/**
 * Error del API con el detalle que hace falta para decidir qué enseñar.
 *
 * `code` sale del cuerpo que manda el API (`invalid_token`, `forbidden`,
 * `authz_unavailable`, …) o se sintetiza aquí para lo que nunca llegó a ser una
 * respuesta (`network`, `timeout`, `not_configured`, `bad_response`).
 */
export class ApiError extends Error {
  constructor(message, { status = 0, code = 'unknown', cause } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (cause !== undefined) this.cause = cause;
  }
}

/**
 * Dirección del API, sin barra final.
 *
 * Se lee en CADA llamada y no al cargar el módulo: así una prueba puede cambiarla
 * y, sobre todo, así el fallo por falta de configuración se ve donde se usa y no
 * como un módulo que explota al importarse (que en una app con `React.lazy` sale
 * como una pantalla en blanco sin causa aparente).
 */
export function apiBaseUrl() {
  return (process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');
}

/**
 * URL completa con sus parámetros.
 *
 * ⚠️ Los valores vacíos NO viajan. `undefined`, `null` y `''` se omiten porque un
 * filtro en blanco no es un filtro, y mandar `category=` obligaría al API a
 * decidir si eso significa "todas" o "las que se llaman cadena vacía". `0` y
 * `false` SÍ viajan: son valores, no ausencias.
 */
export function buildUrl(path, query) {
  const base = apiBaseUrl();
  if (!base) {
    throw new ApiError(
      'Falta REACT_APP_API_BASE_URL: no hay a dónde preguntar.',
      { code: 'not_configured' },
    );
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query || {})) {
    if (v === undefined || v === null || v === '') continue;
    qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `${base}${p}?${s}` : `${base}${p}`;
}

/**
 * El token de la sesión actual, o `null` si no hay nadie dentro.
 *
 * ⚠️ SE ESPERA A QUE FIREBASE RESTAURE LA SESIÓN ANTES DE MIRAR.
 *
 * `auth.currentUser` es SÍNCRONO y arranca en `null`: al abrir la app, Firebase
 * tarda unos milisegundos en releer la sesión de IndexedDB. Una pantalla que
 * llame al API en su primer render sale por tanto SIN token, el API responde
 * 401 y la pantalla lo interpreta como "no existe".
 *
 * No es teórico: así se rompía el perfil público de un taller abierto desde un
 * enlace compartido, que decía "no encontrado" y devolvía al directorio. Con la
 * sesión ya restaurada, ese mismo enlace funcionaba. El fallo solo aparece
 * entrando en frío, que es justo como llega alguien de fuera.
 *
 * `authStateReady()` resuelve de inmediato cuando la sesión ya está resuelta,
 * así que no cuesta nada en las llamadas siguientes. Va aquí, en el único punto
 * por donde salen TODAS las peticiones, y no en cada pantalla: repartirlo sería
 * dejar que la próxima nazca con el mismo fallo.
 *
 * Devuelve `{ uid, token }` del MISMO usuario: el uid sale del objeto sobre el
 * que se pidió el token, no de `auth.currentUser` releído después. Si alguien
 * cambia de cuenta mientras el token se refresca, releerlo daría un uid que no
 * corresponde al token que se está a punto de mandar.
 */
async function bearerToken() {
  if (typeof auth?.authStateReady === 'function') {
    // Si esto falla, se sigue: un problema al esperar no debe impedir intentar
    // la llamada, que como mucho saldrá sin token igual que antes.
    await auth.authStateReady().catch(() => {});
  }
  const user = auth?.currentUser;
  if (!user || typeof user.getIdToken !== 'function') return { uid: '', token: null };
  try {
    return { uid: user.uid || '', token: await user.getIdToken() };
  } catch (err) {
    // No se llama sin token "a ver si cuela": el API respondería 401 y el
    // usuario vería "sesión inválida" cuando lo que falló fue refrescar.
    throw new ApiError('No se pudo obtener el token de sesión.', { code: 'auth_unavailable', cause: err });
  }
}

/**
 * GET idénticos que están EN VUELO ahora mismo, por `uid|method|url`.
 *
 * NO es un caché con plazo: es coalescencia. La entrada se borra en cuanto la
 * promesa se asienta, así que nadie recibe jamás un dato de una petición ya
 * terminada. Existe porque el nav está montado en todas las pantallas y repite
 * las mismas listas que la pantalla acaba de pedir, y cada viaje al API cuesta
 * 0.33-0.42 s desde República Dominicana.
 *
 * Cuatro reglas, y las cuatro tienen su porqué:
 *
 *  1. SOLO `GET`. Dos POST idénticos son dos intenciones (dos artículos al
 *     carrito, dos citas): compartirlos perdería una.
 *  2. El `signal` del llamador NO cancela la petición compartida, solo lo
 *     DESATA a él (ver `conCancelacion`). El peligro que preocupaba —una
 *     pantalla que se desmonta y aborta dejando sin datos a otra que sigue
 *     viva— era real, pero la conclusión de excluirlas era demasiado ancha:
 *     dejaba fuera del caché a `Vehicles`, `Marketplace`, `VehicleDetail` y
 *     `VehicleForm`, que son las más visitadas, y volver a ellas no ahorraba
 *     NADA (558/370/706 ms medidos, puro ruido, contra 61 ms de `/home`).
 *  3. La clave LLEVA EL uid. Sin él, un cambio de cuenta en la misma pestaña
 *     serviría el perfil del usuario anterior.
 *  4. Se suelta con `.finally()`, no con `.then()`: un GET que falla no puede
 *     quedarse pegado repitiendo su error a todo el que pregunte después.
 */
const getsEnVuelo = new Map();

// ════════════════════════════════════════════════════════════════════════════
// LO ÚLTIMO QUE CONTESTÓ CADA GET — para que una pantalla ya vista pinte YA
//
// La coalescencia de arriba solo ahorra si dos peticiones coinciden en el aire.
// Volver a una pantalla treinta segundos después no coincide con nada: se
// vuelve a esperar el viaje entero (0,33-0,42 s desde República Dominicana, y
// bastante más en 4G lenta) mirando un hueco vacío.
//
// Esto guarda la ÚLTIMA respuesta de cada GET y la sirve al instante mientras
// sea reciente, refrescándola por detrás. La pantalla aparece pintada y el dato
// no envejece más de un plazo.
//
// ─── EL PLAZO: 10 SEGUNDOS, Y ES CORTO A PROPÓSITO ──────────────────────────
//
// No se elige por cuánto se ahorra sino por cuánto se puede mentir. Diez
// segundos cubren lo que este caché existe para cubrir —volver atrás, cambiar
// de pestaña dentro de un flujo, el nav que repite la lista que la pantalla
// acaba de pedir— y no llegan a cubrir un flujo de trabajo entero.
//
// ⚠️ Y hay un motivo concreto para no estirarlo: ESTAMOS EN MEDIO DE UNA
// MIGRACIÓN, y hay pantallas que todavía escriben DIRECTAS a Firestore sin
// pasar por aquí. Esas escrituras no invalidan nada, así que el plazo es
// exactamente la ventana en la que alguien podría guardar algo por el camino
// viejo y volver a una lista que aún no lo tiene. Diez segundos es lo que se
// acepta perder; treinta ya alcanzaría a un "guardo y voy a mirarlo", que es
// justo el bug de "dice guardado y no cambia nada" que no se puede repetir.
//
// ─── LO QUE NO ENTRA AQUÍ NUNCA ─────────────────────────────────────────────
//
//   · `/users/me/profile` — `EditProfile` guarda y recarga acto seguido.
//     Servirle diez segundos de antigüedad es reproducir ese mismo bug.
//   · `/cart` y `/orders` — el usuario añade algo y espera VERLO. Un carrito
//     con un artículo de menos no se lee como "va con retraso", se lee como
//     "se perdió".
//
// ─── LA INVALIDACIÓN ES NUCLEAR, Y ES DELIBERADO ────────────────────────────
//
// Cualquier petición que no sea GET vacía el caché ENTERO. La alternativa —un
// mapa de "esta ruta escrita invalida estas otras"— es una tabla que alguien
// tiene que acordarse de ampliar cada vez que nace un endpoint, y la entrada
// que se olvide no da error: da datos viejos en silencio, que es el fallo que
// no se ve hasta que lo ve un cliente.
//
// Se vacía al ASENTARSE la escritura y no al lanzarla: si se hiciera antes, un
// GET disparado mientras la escritura viaja volvería a guardar la foto de
// ANTES, y ese sería el dato que se sirve después.
//
// ⚠️ CON UNA SOLA EXCEPCIÓN, Y ESTÁ MEDIDA: la telemetría (ver
// `ESCRITURAS_QUE_NO_CAMBIAN_NADA`). Sale un `POST` de analítica al entrar a
// CADA pantalla, así que la regla nuclear estaba tirando los datos del usuario
// cada vez que el usuario miraba algo: 466 ms contra 61 ms en la vuelta a
// `/home`, medido contra producción. Una estadística no es estado del usuario.
//
// ─── OTRAS DOS REGLAS QUE VIENEN DE ARRIBA ──────────────────────────────────
//
//   · La clave lleva el uid (la misma que la coalescencia). Además, un cambio
//     de uid vacía todo: cerrar sesión o cambiar de cuenta no deja ni un resto.
//   · Solo GET, igual que la coalescencia. El `signal` del llamador ya NO
//     excluye: corta a quien canceló, no a la petición (ver `conCancelacion`).
//
// NOTA: se guarda el payload TAL CUAL, sin copiarlo, igual que hace la
// coalescencia con los llamadores simultáneos. Es seguro porque el payload
// crudo no sale de esta capa: los módulos de `src/api/*.js` lo traducen a
// objetos nuevos (`adaptProduct`, `adaptOrder`…) antes de que una pantalla lo
// toque. Si algún día una pantalla recibiera el crudo y lo ordenara en sitio,
// aquí haría falta una copia.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Cuánto vale una respuesta guardada. Se exporta para que la prueba lo nombre.
 *
 * 30 s por DECISIÓN DEL DUEÑO (2026-09-03), subido desde los 10 s iniciales para
 * que moverse entre pantallas se sienta instantáneo: la app no comparte datos
 * entre pantallas (no hay contexto de vehículos ni de citas) y el árbol se
 * remonta entero en cada cambio de ruta, así que cada pantalla vuelve a pedir lo
 * que la anterior ya tenía. Esta ventana es hoy lo único que amortigua eso.
 *
 * LO QUE SE ACEPTA A CAMBIO, dicho entero:
 *   · Un cambio hecho por OTRO (el taller que confirma tu cita, un pedido que
 *     cambia de estado) puede tardar hasta 30 s en verse mientras navegas.
 *   · Lo mismo con lo que se escribe DIRECTO a Firestore sin pasar por aquí
 *     —quedan sobre todo pantallas de admin—, porque esas no invalidan nada.
 *
 * LO QUE NO SE ARRIESGA, y por eso 30 s es tolerable y no un salto al vacío:
 *   · Todo lo que se escribe POR AQUÍ vacía el caché entero al asentarse, así
 *     que "guardo y voy a mirarlo" sigue mostrando lo nuevo.
 *   · El perfil, el carrito y los pedidos NUNCA se guardan (ver la lista de
 *     excluidas): son justo donde ver algo viejo se lee como un fallo.
 *
 * El techo de la prueba sigue en 30 s: subirlo más deja de ser "volver atrás sin
 * esperar" y empieza a alcanzar flujos de trabajo enteros.
 */
export const GET_CACHE_MS = 30000;

/**
 * CUÁNTO VALE LO GUARDADO, SEGÚN QUÉ ES (2026-10-28, a pedido del dueño: «la
 * caché no está funcionando; alarga el tiempo que dura»).
 *
 * Medido antes de tocar: con un solo plazo de 30 s contados desde que se trajo
 * el dato, casi ninguna vuelta acertaba (un paseo por la app dura minutos), y
 * al recargar o reabrir la PWA no quedaba NADA, porque todo vivía en memoria.
 *
 * Cada GET se sirve de lo guardado mientras no supere su `maxEdad`, y SIEMPRE
 * se refresca por detrás (eso no cambia): la pantalla pinta al instante y la
 * siguiente visita ya trae lo nuevo. Lo que cambia es cuánto puede tener lo que
 * se pinta, y eso depende de quién más puede cambiarlo:
 *
 *   · CATÁLOGO Y PERFILES PÚBLICOS — 24 h, y sobreviven a cerrar la app
 *     (`localStorage`). Lo cambian otros y casi nunca; lo que sale caro es
 *     esperarlos. El stock que se pinta puede ir atrasado, pero no vende de
 *     más: `createOrder` lo vuelve a comprobar en el servidor.
 *   · LO TUYO (carros, historial, combustible, favoritos) — 10 min, y
 *     sobrevive a recargar dentro de la misma pestaña (`sessionStorage`, que
 *     se borra al cerrarla: son datos personales). Casi todo lo escribes tú,
 *     por aquí, y escribir vacía el caché entero.
 *   · CITAS, SOPORTE Y MENSAJES — 10 s, solo en memoria. Lo cambia OTRO (el
 *     taller que confirma) y verlo tarde se lee como un fallo.
 *   · TODO LO DEMÁS — los 30 s de siempre, solo en memoria.
 *
 * 🔴 VA POR INCLUSIÓN, IGUAL QUE LA LISTA DE ESCRITURAS DE MÁS ABAJO: una ruta
 * nueva cae en «todo lo demás» hasta que alguien decida otra cosa. Alargar el
 * plazo de algo tiene que ser una decisión, no un descuido.
 */
const MIN = 60 * 1000;
const POLITICAS_CACHE = [
  {
    nombre: 'corto',
    maxEdad: 10 * 1000,
    guardar: null,
    rutas: [/^\/appointments(\/|$)/, /^\/support(\/|$)/, /^\/diagnoses\/[^/]+\/messages$/],
  },
  {
    nombre: 'publico',
    maxEdad: 24 * 60 * MIN,
    guardar: 'localStorage',
    rutas: [
      /^\/products(\/[^/]+(\/reviews)?)?$/,
      // `/workshops/<id>` a secas NO: es también la ficha que lee el dueño en
      // `WorkshopBilling`, y su suscripción la cambia el servidor (PayPal).
      /^\/workshops(\/[^/]+\/(storefront|products))?$/,
      /^\/mechanics$/,
      /^\/rankings\/(workshops|mechanics)$/,
      /^\/listings(\/[^/]+)?$/,
      /^\/users\/[^/]+\/profile$/,
      /^\/announcements$/,
    ],
  },
  {
    nombre: 'propio',
    maxEdad: 10 * MIN,
    guardar: 'sessionStorage',
    rutas: [
      /^\/vehicles(\/[^/]+(\/(records|fuel-logs|diagnoses|inspections|shares))?)?$/,
      /^\/fleet-records$/,
      /^\/users\/me\/(favorites|wishlist|saved-listings|nav-config)$/,
    ],
  },
];
const POLITICA_POR_DEFECTO = { nombre: 'normal', maxEdad: GET_CACHE_MS, guardar: null };

/** La política de una ruta. `/users/me/profile` no casa con `/users/[^/]+/profile`: lo excluye antes `esCacheable`. */
export function politicaDeCache(ruta) {
  return POLITICAS_CACHE.find((p) => p.rutas.some((re) => re.test(ruta))) || POLITICA_POR_DEFECTO;
}

// ─── LO GUARDADO EN EL NAVEGADOR ────────────────────────────────────────────
//
// Cada entrada va en su propia clave (`mkc:<uid|GET|url>`) para no reescribir
// todo en cada respuesta. Todo acceso va envuelto: en una ventana privada, con
// el almacenamiento bloqueado o lleno, el storage LANZA, y eso no puede costar
// la petición. Si falla, este caché se comporta como antes: solo memoria.
const PREFIJO_STORAGE = 'mkc:';
const CLAVE_UID_STORAGE = 'mkc-uid';
/** Una respuesta más grande que esto no se guarda en el navegador (sí en memoria). */
const MAX_BYTES_ENTRADA = 200 * 1024;
/** Máximo de respuestas guardadas por almacén; al pasarse se tiran las más viejas. */
const MAX_ENTRADAS_STORAGE = 60;

function almacen(tipo) {
  try {
    return typeof window !== 'undefined' && tipo ? window[tipo] || null : null;
  } catch {
    return null;                               // acceder a `window.localStorage` ya puede lanzar
  }
}

function leerGuardado(tipo, clave) {
  const s = almacen(tipo);
  if (!s) return null;
  try {
    const crudo = s.getItem(PREFIJO_STORAGE + clave);
    if (!crudo) return null;
    const entrada = JSON.parse(crudo);
    return entrada && typeof entrada.en === 'number' ? entrada : null;
  } catch {
    return null;
  }
}

function clavesPropias(s) {
  const claves = [];
  for (let i = 0; i < s.length; i += 1) {
    const k = s.key(i);
    if (k && k.startsWith(PREFIJO_STORAGE)) claves.push(k);
  }
  return claves;
}

function escribirGuardado(tipo, clave, entrada) {
  const s = almacen(tipo);
  if (!s) return;
  try {
    const crudo = JSON.stringify(entrada);
    if (crudo.length > MAX_BYTES_ENTRADA) return;
    const claves = clavesPropias(s).filter((k) => k !== PREFIJO_STORAGE + clave);
    if (claves.length >= MAX_ENTRADAS_STORAGE) {
      const edades = claves.map((k) => {
        try { return [k, JSON.parse(s.getItem(k)).en || 0]; } catch { return [k, 0]; }
      }).sort((a, b) => a[1] - b[1]);
      edades.slice(0, claves.length - MAX_ENTRADAS_STORAGE + 1).forEach(([k]) => s.removeItem(k));
    }
    s.setItem(PREFIJO_STORAGE + clave, crudo);
  } catch {
    // Lleno o bloqueado: se queda solo en memoria. No se reintenta.
  }
}

function vaciarGuardados() {
  ['localStorage', 'sessionStorage'].forEach((tipo) => {
    const s = almacen(tipo);
    if (!s) return;
    try { clavesPropias(s).forEach((k) => s.removeItem(k)); } catch { /* bloqueado */ }
  });
}

function leerUidGuardado() {
  const s = almacen('localStorage');
  if (!s) return null;
  try { return s.getItem(CLAVE_UID_STORAGE); } catch { return null; }
}

function escribirUidGuardado(uid) {
  const s = almacen('localStorage');
  if (!s) return;
  try { s.setItem(CLAVE_UID_STORAGE, uid); } catch { /* bloqueado */ }
}

/** Guarda en memoria y, si la política lo pide, en el navegador. */
function guardarRespuesta(clave, politica, payload) {
  const entrada = { payload, en: Date.now() };
  getsRecientes.set(clave, entrada);
  if (politica.guardar) escribirGuardado(politica.guardar, clave, entrada);
}

/** `uid|method|url` → `{ payload, en }`. */
const getsRecientes = new Map();
/** Claves con un refresco por detrás en curso (para no lanzar dos). */
const revalidando = new Set();
/**
 * Último uid que llenó el caché; si cambia, lo de dentro no es de esta cuenta.
 * Se lee del navegador al arrancar: sin eso, la primera petición tras recargar
 * vería `null !== uid` y tiraría justo lo que se guardó para sobrevivir a recargar.
 */
let uidDelCache = leerUidGuardado();
/**
 * Sube en cada vaciado. Una respuesta que salió ANTES de un vaciado y llega
 * DESPUÉS no puede guardarse: sería reponer justo lo que se acaba de tirar.
 */
let generacion = 0;

/** Rutas que no se guardan nunca. Se comparan contra el camino, no contra la URL. */
const RUTAS_SIN_CACHE = [
  /^\/users\/me\/profile$/,
  /^\/cart(\/|$)/,
  /^\/orders(\/|$)/,
];

const esCacheable = (ruta) => !RUTAS_SIN_CACHE.some((re) => re.test(ruta));

/**
 * LAS ÚNICAS ESCRITURAS QUE NO TIRAN LO LEÍDO.
 *
 * La invalidación nuclear de arriba es correcta para todo lo que el usuario
 * escribe, y no se toca. Pero mete en el mismo saco algo que no es del usuario:
 * la ANALÍTICA. Al entrar a cada pantalla sale un `POST /analytics/screen-views`,
 * y al escribir en el buscador un `POST /analytics/searches`. Ninguno de los dos
 * cambia un vehículo, una cita, un pedido ni un perfil: son telemetría, no
 * estado. Con la regla nuclear, contar que alguien MIRÓ una pantalla le borraba
 * los datos que acababa de traer y le obligaba a esperar el viaje entero otra
 * vez — 466 ms contra 61 ms al volver a `/home`, medido contra producción.
 *
 * 🔴 LA LISTA ES DE LAS QUE **NO** INVALIDAN, Y ESO ES LO IMPORTANTE.
 *
 * La lista contraria (las que SÍ invalidan) sería exactamente la tabla contra la
 * que avisa el recuadro de arriba: la ruta nueva que nadie se acuerda de añadir
 * no daría error, daría datos viejos en silencio. Así, al revés, lo que se
 * olvida cae del lado seguro: **una ruta nueva invalida por defecto**, y como
 * mucho cuesta una petición de más. Para ganarse un puesto aquí una ruta tiene
 * que cumplir las dos: ser una escritura, y no cambiar NINGÚN dato que el
 * usuario pueda leer después.
 *
 * Se enumeran las dos rutas exactas en vez de exentar `/analytics/*` entero, por
 * la misma razón: el día que nazca un endpoint de analítica que sí toque algo
 * legible, el comodín ya lo habría exentado sin que nadie lo decidiera.
 *
 * LO QUE SE ACEPTA A CAMBIO, dicho entero: el panel de analítica del admin
 * (`GET /admin/analytics/*`) lee los contadores que estos POST suman, así que
 * puede verlos hasta 30 s viejos. Es un acumulado agregado en una pantalla de
 * consulta; nadie escribe ahí esperando ver su propio cambio.
 */
const ESCRITURAS_QUE_NO_CAMBIAN_NADA = [
  /^\/analytics\/screen-views$/,
  /^\/analytics\/searches$/,
  // 2026-10-28, medido: abrir un producto o el perfil de un taller o mecánico
  // manda su contador de visitas, y cada uno vaciaba el caché ENTERO — o sea,
  // navegar por el catálogo tiraba el catálogo en cada tarjeta que se abría.
  // Lo único legible que cambian es el propio contador (las vistas del
  // producto, la gráfica de visitas del taller), que puede verse atrasado
  // hasta que el refresco por detrás lo traiga.
  /^\/products\/[^/]+\/view$/,
  /^\/profile-metrics\/(views|clicks)$/,
];

const invalidaLoLeido = (ruta) => !ESCRITURAS_QUE_NO_CAMBIAN_NADA.some((re) => re.test(ruta));

/** Vacía lo guardado. La usa la invalidación por escritura, el cambio de cuenta y las pruebas. */
export function limpiarCacheGet() {
  generacion += 1;
  getsRecientes.clear();
  revalidando.clear();
  vaciarGuardados();
}

/**
 * Vuelve a pedir un GET que se acaba de servir del caché, sin que nadie espere.
 *
 * Un fallo aquí no se cuenta a nadie: quien preguntó ya tiene su respuesta y no
 * se le puede convertir en error lo que fue un refresco de cortesía.
 */
function revalidarPorDetras({ clave, politica, url, token, timeoutMs, doFetch }) {
  if (revalidando.has(clave) || getsEnVuelo.has(clave)) return;
  revalidando.add(clave);
  const gen = generacion;
  ejecutar({ url, method: 'GET', token, timeoutMs, doFetch })
    .then((payload) => {
      if (gen === generacion) guardarRespuesta(clave, politica, payload);
    })
    .catch(() => {})
    .finally(() => { revalidando.delete(clave); });
}

/**
 * CANCELAR CORTA AL QUE ESCUCHA, NO A LA PETICIÓN.
 *
 * Devuelve una promesa que sigue a `peticion` salvo que `signal` se dispare: en
 * ese caso rechaza con el MISMO error de siempre (`ApiError` con código
 * `aborted`) y se desentiende. La petición de abajo ni se entera: sigue su
 * camino, se la reparte a quien más la esperaba y su respuesta se guarda.
 *
 * ─── POR QUÉ NO SE ABORTA LA RED CUANDO EL QUE CANCELÓ ERA EL ÚLTIMO ─────────
 *
 * Se podría llevar la cuenta de cuántos esperan y abortar al llegar a cero. Se
 * decidió DEJARLA TERMINAR, y estos son los motivos, en orden:
 *
 *  1. La cuenta de espectadores es una carrera. Entre que el último suelta y la
 *     respuesta aterriza puede llegar otro llamador —es literalmente el caso
 *     que este módulo existe para servir: dos pantallas pidiendo lo mismo con
 *     milisegundos de diferencia— y se encontraría una petición abortada o, si
 *     se relanza, dos viajes donde había uno. Compartir y abortar por consenso
 *     es justo la clase de estado repartido que produce fallos que no se ven.
 *  2. Tirarla es tirar lo que se vino a buscar. El aborto ocurre AL DESMONTAR,
 *     o sea cuando el usuario ya navegó, con la petición normalmente casi
 *     entera (el viaje son 0,33-0,42 s desde República Dominicana). Y el que se
 *     fue vuelve: el 58 % de las peticiones de un recorrido repiten una URL ya
 *     pedida. Dejarla terminar convierte esa vuelta en 61 ms.
 *  3. El gasto está acotado y no es nuevo: el plazo interno (`API_TIMEOUT_MS`)
 *     sigue corriendo, así que nada puede quedarse colgado, y lo que llega es
 *     un JSON de listado. Abortar ahorraría la cola de una descarga pequeña a
 *     cambio de pagar el viaje entero otra vez treinta segundos después.
 *
 * LO QUE SE ACEPTA A CAMBIO: en `Marketplace`, cambiar filtros deprisa deja
 * varias búsquedas en el aire en vez de cancelarlas. El buscador ya va con
 * rebote, así que son pocas, y cada una queda guardada bajo SU combinación de
 * filtros: volver a un filtro que ya se probó pinta al instante.
 */
function conCancelacion(peticion, signal) {
  if (!signal) return peticion;
  if (signal.aborted) {
    // Nadie más va a mirar esta promesa por este camino; sin un `catch` de
    // cortesía, un fallo suyo se contaría como rechazo no atendido.
    peticion.catch(() => {});
    return Promise.reject(new ApiError('Petición cancelada.', { code: 'aborted' }));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new ApiError('Petición cancelada.', { code: 'aborted' }));
    signal.addEventListener('abort', onAbort);
    const soltar = () => signal.removeEventListener('abort', onAbort);
    peticion.then(
      (valor) => { soltar(); resolve(valor); },
      (err) => { soltar(); reject(err); },
    );
  });
}

/**
 * Una petición al API.
 *
 * `fetchImpl` se inyecta en pruebas; en producción es el `fetch` del navegador.
 * `signal` del llamador se respeta ADEMÁS del plazo interno: una pantalla que se
 * desmonta cancela, y el plazo sigue corriendo por su cuenta. En los GET la
 * cancelación corta al llamador y no a la petición (ver `conCancelacion`); en
 * las escrituras, que no se comparten con nadie, sigue abortando la red.
 */
export async function apiFetch(path, {
  method = 'GET',
  query,
  body,
  signal,
  timeoutMs = API_TIMEOUT_MS,
  fetchImpl,
} = {}) {
  const url = buildUrl(path, query);           // lanza `not_configured` sin llamar a nadie
  // El token se pide ANTES de calcular la clave a propósito: el uid solo se sabe
  // con la sesión ya restaurada (`bearerToken` espera a `authStateReady`), y
  // una clave sin uid mezclaría las cuentas de una misma pestaña. Cuesta nada:
  // `getIdToken()` sirve de su propio caché mientras el token siga vigente. Que
  // `bearerToken()` pueda lanzar tampoco estorba: entonces no hay petición que
  // compartir, así que no se ha llegado a tocar el mapa.
  const { uid, token } = await bearerToken();
  // Cambio de cuenta (o cierre de sesión, que deja el uid vacío): lo guardado ya
  // no es de quien está dentro. Se comprueba aquí, en el sitio por donde pasan
  // todas las peticiones, y no confiando en que alguien se acuerde de avisar.
  if (uid !== uidDelCache) {
    limpiarCacheGet();
    uidDelCache = uid;
    escribirUidGuardado(uid);
  }
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) throw new ApiError('No hay implementación de fetch disponible.', { code: 'not_configured' });

  // Todo GET se comparte y se guarda, TRAIGA O NO `signal`. Quien cancela se
  // desata de la respuesta (`conCancelacion`) en vez de quedarse fuera del
  // caché: las cuatro pantallas más visitadas cancelan al desmontarse, y eso las
  // dejaba pagando el viaje entero en cada vuelta.
  const compartible = method === 'GET';
  const clave = compartible ? `${uid}|${method}|${url}` : null;
  if (compartible) {
    const enVuelo = getsEnVuelo.get(clave);
    if (enVuelo) return conCancelacion(enVuelo, signal);
  }

  // El caché va DESPUÉS de la coalescencia: si hay una idéntica en el aire, esa
  // es más fresca que lo guardado y llega igual de pronto.
  const ruta = path.startsWith('/') ? path : `/${path}`;
  const cacheable = compartible && esCacheable(ruta);
  const politica = politicaDeCache(ruta);
  if (cacheable) {
    // Memoria primero; si no está (recién recargada la página), lo guardado en
    // el navegador, que se sube a memoria para la siguiente.
    let guardado = getsRecientes.get(clave);
    if (!guardado && politica.guardar) {
      guardado = leerGuardado(politica.guardar, clave);
      if (guardado) getsRecientes.set(clave, guardado);
    }
    if (guardado && Date.now() - guardado.en < politica.maxEdad) {
      revalidarPorDetras({ clave, politica, url, token, timeoutMs, doFetch });
      // Pasa por `conCancelacion` como todo lo demás: quien llamó con un signal
      // ya abortado tiene que recibir su `aborted`, no un dato que ya no espera.
      return conCancelacion(Promise.resolve(guardado.payload), signal);
    }
  }

  const gen = generacion;
  // 🔴 El `signal` del llamador NO baja a la petición cuando es compartible: es
  // de UNO de los que esperan, y abortar aquí cancelaría los datos del resto.
  const peticion = ejecutar({
    url, method, token, body, timeoutMs, doFetch,
    signal: compartible ? undefined : signal,
  });
  if (compartible) {
    getsEnVuelo.set(clave, peticion);
    peticion
      .then((payload) => {
        // `gen` distinto = hubo un vaciado mientras esto viajaba (una escritura,
        // un cambio de cuenta). Guardarlo ahora repondría la foto de antes.
        if (cacheable && gen === generacion) guardarRespuesta(clave, politica, payload);
      })
      .finally(() => { if (getsEnVuelo.get(clave) === peticion) getsEnVuelo.delete(clave); })
      .catch(() => {});                        // el fallo lo trata quien llamó, no esta rama
  }
  if (method !== 'GET' && invalidaLoLeido(ruta)) {
    // Cualquier escritura invalida TODO lo leído (ver el recuadro). Al asentarse
    // y no al lanzarse, para que un GET que viajaba en paralelo no reponga la
    // foto de antes de escribir. La única excepción es la telemetría, que no
    // cambia nada que el usuario lea (ver `ESCRITURAS_QUE_NO_CAMBIAN_NADA`).
    peticion.finally(() => limpiarCacheGet()).catch(() => {});
  }
  return compartible ? conCancelacion(peticion, signal) : peticion;
}

async function ejecutar({ url, method, token, body, signal, timeoutMs, doFetch }) {
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onAbort);
  }
  let vencido = false;
  const timer = setTimeout(() => { vencido = true; controller.abort(); }, timeoutMs);

  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res;
  try {
    res = await doFetch(url, {
      method,
      headers,
      signal: controller.signal,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    if (vencido) throw new ApiError(`El API no respondió en ${timeoutMs} ms.`, { code: 'timeout', cause: err });
    if (err && err.name === 'AbortError') throw new ApiError('Petición cancelada.', { code: 'aborted', cause: err });
    // `fetch` solo rechaza por fallo de RED; un 500 llega como respuesta.
    throw new ApiError('No se pudo contactar el servidor.', { code: 'network', cause: err });
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }

  let payload = null;
  let parseo = null;
  try {
    payload = await res.json();
  } catch (err) {
    parseo = err;
  }

  if (!res.ok) {
    const code = (payload && typeof payload.error === 'string' && payload.error) || `http_${res.status}`;
    const msg = (payload && typeof payload.message === 'string' && payload.message) || `El API respondió ${res.status}.`;
    throw new ApiError(msg, { status: res.status, code });
  }

  if (parseo) {
    // 200 que no es JSON. El caso real: el hosting devuelve `index.html` para una
    // ruta que no existe, así que la petición "va bien" y no trae datos. Tratarlo
    // como respuesta vacía dejaría un catálogo en blanco sin ningún error.
    throw new ApiError('El API devolvió algo que no es JSON.', { status: res.status, code: 'bad_response', cause: parseo });
  }

  return payload;
}
